import React, { useRef, useEffect, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";

ChartJS.register(ArcElement, Tooltip);

interface GeneralConfig {
  values: number[];
  labels: string[];
  colors: string[];
}
interface Props {
  container: HTMLElement;
}

// Render a single legend item
const LegendItem = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div className="flex items-center mb-4 last:mb-0">
    <div
      className="mr-2.5 rounded-full w-3 h-3"
      style={{
        backgroundColor: color,
      }}
    />
    <span className="text-base">
      {label} ({value.toString().replace(".", ",")} %)
    </span>
  </div>
);

const ChartComponent: React.FC<Props> = ({ container }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Load widget configuration from global window object
  const config =
    (window.WIDGETS_CONFIG?.[container.dataset.widget_id] as Partial<GeneralConfig>) ?? {};
  const labels = config.labels ?? [];
  const values = config.values ?? [];
  const colors = config.colors ?? [];

  // Safely zip the data into one array
  const entries = labels.map((label, i) => ({
    label,
    value: values[i] ?? 0,
    color: colors[i] ?? "#ccc",
  }));

  // Prepare chart data
  const data: ChartData<"doughnut"> = {
    labels: entries.map((e) => e.label),
    datasets: [
      {
        data: entries.map((e) => e.value),
        backgroundColor: entries.map((e) => e.color),
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  // Define chart options
  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    cutout: "70%", // donut hole size
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value = ctx.parsed.toString().replace(".", ",");
            return ` ${value} %`;
          },
        },
      },
    },
  };

  // Observe when the chart enters the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect(); // Only run once
        }
      },
      { threshold: 0.3 } // Trigger when 30% is visible
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="flex flex-col sm:flex-row sm:items-center gap-8 sm:gap-12 mb-12">
      <div className="w-64 h-64">{visible && <Doughnut data={data} options={options} />}</div>
      <div>
        {entries.map((entry) => (
          <LegendItem
            key={entry.label}
            label={entry.label}
            value={entry.value}
            color={entry.color}
          />
        ))}
      </div>
    </div>
  );
};

export default ChartComponent;
